import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useRoute } from 'wouter';
import DynamicTable from '../DynamicTable';
import { showMessage } from '../Toastr/ToastService';
import {
  TABLE_IMPORT_TEMPLATE,
  TABLE_REFUELINGS_IMPORT,
  TABLE_REFUELINGS_IMPORT_RECORDS,
} from '../../Services/TableService';
import { URL_EXPENSE_IMPORT } from '../../router/constants';
import PermissionsService, {
  PERMISSION_IMPORTED_FILE,
  PERMISSION_IMPORTED_RECORD,
  PERMISSION_IMPORTING_TEMPLATE,
} from '../../Services/PermissionsService';
import TabComponent from '../TabComponent';
import { IValidationRule } from '../../Services/FormikValidatorService';
import EditForm from './EditForm';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import { IOption } from '../Forms/CmdField';
import ActionForm from './ActionForm';
import { IGNORE, PENDING } from '../DynamicTable/TableBody/TableRow';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { ITab } from '../TabComponent/interfaces';
import PageHeader from '../PageHeader';

export interface IFilesImportInput {
  indexId: string;
  type: string;
  subtype?: string;
  title: string;
  placeholder: string;
  value?: string | undefined;
  values?: IOption[];
  firstDisabled: boolean;
  multiple: boolean;
  disabled: boolean;
  readonly: boolean;
  sendOnChange: boolean;
  rules: IValidationRule[];
  section: any;
  inputGroup: any;
}

export const tabsByPermission = (tabs: string[], active: string): ITab[] => {
  const enabledTabs: ITab[] = [];
  tabs.forEach((tabItem: string) => {
    if (
      tabItem === TABLE_REFUELINGS_IMPORT_RECORDS &&
      PermissionsService.hasAccess(PERMISSION_IMPORTED_RECORD, ['read'])
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: `${URL_EXPENSE_IMPORT}/expense/import/record`,
      });
    }
    if (
      tabItem === TABLE_REFUELINGS_IMPORT &&
      PermissionsService.hasAccess(PERMISSION_IMPORTED_FILE, ['read'])
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: `${URL_EXPENSE_IMPORT}/expense/import`,
      });
    }
    if (
      tabItem === TABLE_IMPORT_TEMPLATE &&
      PermissionsService.hasAccess(PERMISSION_IMPORTING_TEMPLATE, ['read'])
    ) {
      enabledTabs.push({
        name: tabItem,
        isActive: tabItem === active,
        url: `${URL_EXPENSE_IMPORT}/expense/import-template`,
      });
    }
  });
  return enabledTabs;
};

export default function ImportRefuelings(): JSX.Element {
  const query = useUrlQueryParams();
  const { formatMessage: f } = useIntl();
  const [isModalOpened, setIsModalOpened] = React.useState<boolean | string>(false);
  const [isActionOpened, setIsActionOpened] = React.useState<string>('');
  const [itemId, setItemId] = React.useState<number | null>(null);
  const [callTable, setCallTable] = React.useState<boolean>(false);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const [data, setData] = React.useState<IFilesImportInput[]>([]);
  const tabs = React.useState(
    tabsByPermission(
      [TABLE_REFUELINGS_IMPORT_RECORDS, TABLE_REFUELINGS_IMPORT, TABLE_IMPORT_TEMPLATE],
      TABLE_REFUELINGS_IMPORT_RECORDS
    )
  )[0];
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${tabs.filter((item) => item.isActive)[0].url}/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const buttons = React.useState<{
    [key: string]: { open: boolean | string[]; handle?: boolean | string[] | undefined };
  }>({
    [TABLE_REFUELINGS_IMPORT_RECORDS]: {
      open: ['wizardBtn'],
      handle: ['close', 'save'],
    },
  })[0];

  React.useEffect(() => {
    const wizardClose = query.get('wizardclose');

    if (wizardClose) {
      showMessage('', f({ id: 'wizard.toast.cancel.message' }), 'info');
    }
  }, [f, query]);

  React.useEffect(() => {
    if (URL_EXPENSE_IMPORT === pathname || `${URL_EXPENSE_IMPORT}/` === pathname) {
      navigate(`${URL_EXPENSE_IMPORT}/expense/import/record`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  React.useEffect(() => {
    if (pathname === `${URL_EXPENSE_IMPORT}/expense/import/record`) {
      const documentTitle = `${f({ id: 'tab.expense/import/record' })} - ${f({
        id: 'menu.expenseImports',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (pathname === URL_EXPENSE_IMPORT || pathname === `${URL_EXPENSE_IMPORT}/`) {
      setIsModalOpened(false);
      setCustomerId(null);
      setData([]);
      const documentTitle = `${f({ id: 'tab.expense/import/record' })} - ${f({
        id: 'menu.expenseImports',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (params) {
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({
        id: 'tab.expense/import/record',
      })} - ${f({
        id: 'menu.expenseImports',
      })}`;
      setDocumentTitle(documentTitle);

      const customerIdUrl = query.get('customerId');
      if (customerId) {
        setCustomerId(Number(customerIdUrl));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, pathname]);

  React.useEffect(() => {
    if (data.length === 0 && params) {
      const customerIdUrl = query.get('customerId');
      if (customerIdUrl) {
        setCustomerId(Number(customerIdUrl));
      }
      setCallTable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, data.length, pathname]);

  const handleClose = React.useCallback(() => {
    navigate(`${URL_EXPENSE_IMPORT}/expense/import/record`);
    setIsModalOpened(false);
    setIsActionOpened('');
    setData([]);
    setCustomerId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateTable = () => {
    setCallTable(true);
  };

  const handleModal = React.useCallback(
    (formData: any, idValue: number, customer: number, actionType?: string) => {
      if (formData) {
        if (actionType) {
          setIsActionOpened(actionType);
          setItemId(idValue);
        } else {
          setIsModalOpened(true);
        }
        setData(formData);
        setCustomerId(customer);
        setCallTable(false);
      }
    },
    []
  );

  return (
    <div>
      <PageHeader
        title={`${f({ id: 'menu.expenseImports' })}`}
        onlineHelpTopic="import-refuelings"
      />
      <div className="col-12">
        <TabComponent tabs={tabs}>
          <DynamicTable
            name={TABLE_REFUELINGS_IMPORT_RECORDS}
            mustClosed={true}
            buttons={buttons[TABLE_REFUELINGS_IMPORT_RECORDS]}
            dragClassname="drag"
            updateTable={callTable}
            handleForm={handleModal}
            routerParams={params}
          />
        </TabComponent>
      </div>
      {isModalOpened && data && data.length > 0 && params && (
        <div>
          {PermissionsService.hasAccess(PERMISSION_IMPORTED_RECORD, ['update'], customerId) && (
            <EditForm
              id={Number(params.id)}
              customerId={customerId}
              data={data}
              updateTable={updateTable}
              handleClose={handleClose}
            />
          )}
        </div>
      )}
      {(isActionOpened === PENDING || isActionOpened === IGNORE) &&
        data &&
        data.length > 0 &&
        itemId && (
          <div>
            {PermissionsService.hasAccess(PERMISSION_IMPORTED_RECORD, ['update'], customerId) && (
              <ActionForm
                id={itemId}
                customerId={customerId}
                data={data}
                updateTable={updateTable}
                handleClose={handleClose}
                action={isActionOpened}
              />
            )}
          </div>
        )}
    </div>
  );
}
